import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { StoreModule } from "@/constants/store.ts";
import {
  SecurityGapAction,
  SecurityGapType,
  SecurityGapWidgetType,
} from "@/constants/security-gaps.ts";
import type { ISelection } from "@/_store/selector.module.ts";
import { getSelectionAsStringsArray, isMspPortalRoute, navigateToRoute } from "@/_helpers/utils.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import { RouteName } from "@/constants/routes.ts";

export interface SecurityGapItem {
  widgetType: SecurityGapWidgetType;
  securityGapType: SecurityGapType;
  affectedWorkspaces: number;
  actions: SecurityGapAction[];
}

export interface SecurityGapsState {
  securityGapsList: SecurityGapItem[];
  lastDetected: number;
  loading: boolean;
  showSkeletonLoader: boolean;
  workspaces: { name: string; workspaceId: string }[];
  totalWorkspaces: number;
  visitedWorkspaceIds: string[];
  // Navigation state for floating back button
  navigation: {
    showBackButton: boolean;
    workspaceId: string;
    securityGapType: SecurityGapType | string;
  };
}

const defaultSecurityGapsState: SecurityGapsState = {
  securityGapsList: [],
  lastDetected: 0,
  loading: false,
  showSkeletonLoader: false,
  workspaces: [],
  totalWorkspaces: 0,
  visitedWorkspaceIds: [],
  navigation: {
    showBackButton: false,
    workspaceId: "",
    securityGapType: "",
  },
};

// @TODO remove after BE will add socket
const FIX_SECURITY_GAP_TIMEOUT = 3000;

export const useSecurityGapsStore = defineStore(StoreModule.SECURITY_GAPS, {
  state: (): SecurityGapsState => ({ ...defaultSecurityGapsState }),
  persist: {
    paths: ["navigation"], // Only persist navigation state
  },
  actions: {
    async getSecurityGaps(showSkeletonLoader = false) {
      const { status, type, workspaceIds } = useFiltersStore().filters[FilterContext.SECURITY_GAPS];
      const mspRequestConfig = {
        ...api.mspSecurityGaps,
        method: "POST",
        data: {
          workspaceIds: workspaceIds ?? [],
          type: type ?? [],
          status,
        },
      };
      const regularRequestConfig = {
        ...api.securityGaps,
        method: "GET",
        params: {
          type: type?.join(","),
          status,
        },
      };
      const requestConfig = isMspPortalRoute() ? mspRequestConfig : regularRequestConfig;

      try {
        this.showSkeletonLoader = showSkeletonLoader;
        this.loading = true;
        const {
          data: { items, lastDetected },
        } = await axiosInstance.request(requestConfig);

        this.securityGapsList = items;
        this.lastDetected = lastDetected;
      } catch (e) {
        console.error(e);
      } finally {
        this.showSkeletonLoader = false;
        this.loading = false;
      }
    },
    async applySecurityGapAction(payload: {
      action: SecurityGapAction;
      selection: ISelection;
      securityGapType: SecurityGapType;
    }) {
      const apiUrl = isMspPortalRoute() ? api.mspSecurityGapAction : api.securityGapAction;
      try {
        this.loading = true;
        await axiosInstance.request({
          ...apiUrl,
          method: "POST",
          data: {
            action: payload.action,
            selection: getSelectionAsStringsArray(payload.selection, "workspaceId"),
            filter: {
              securityGapType: payload.securityGapType,
            },
          },
        });
        if ([SecurityGapAction.FIX_ALL].includes(payload.action)) {
          await new Promise((resolve) => setTimeout(resolve, FIX_SECURITY_GAP_TIMEOUT));
        }
        await this.getSecurityGaps();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(`snackbar.messages.securityGaps.${payload.action}`)
        );
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async getSecurityGapWorkspaces(securityGapType: SecurityGapType) {
      const apiUrl = isMspPortalRoute()
        ? api.getSecurityGapMspWorkspaces
        : api.getSecurityGapWorkspaces;
      try {
        const { data } = await axiosInstance.request({
          ...apiUrl,
          params: {
            securityGapType,
          },
        });
        this.workspaces = data.items;
        this.totalWorkspaces = data.total;
      } catch (e) {
        console.error(e);
      }
    },
    async triggerRefresh(securityGapType: SecurityGapType) {
      const apiUrl = isMspPortalRoute() ? api.mspSecurityGapRefresh : api.securityGapRefresh;
      try {
        this.loading = true;
        await axiosInstance.request({
          ...apiUrl,
          params: {
            securityGapType,
          },
          data: { workspaceIds: this.visitedWorkspaceIds },
        });
        await new Promise((resolve) => setTimeout(resolve, FIX_SECURITY_GAP_TIMEOUT));
        await this.getSecurityGaps();
        this.clearVisitedWorkspaceIds();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    addVisitedWorkspaceId(workspaceId: string) {
      if (!this.visitedWorkspaceIds.includes(workspaceId)) {
        this.visitedWorkspaceIds.push(workspaceId);
      }
    },
    clearVisitedWorkspaceIds() {
      this.visitedWorkspaceIds = [];
    },
    // Navigation actions for floating back button
    setNavigationFromSecurityGaps(securityGapItem: SecurityGapItem, workspaceId: string) {
      // Add delay to wait till the new workspace is loaded
      setTimeout(() => {
        this.navigation.showBackButton = true;
        this.navigation.workspaceId = workspaceId;
        this.navigation.securityGapType = securityGapItem.securityGapType;
      }, 1500);
    },
    clearNavigationState() {
      this.navigation.showBackButton = false;
      this.navigation.workspaceId = "";
      this.navigation.securityGapType = "";
    },
    async goBackToSecurityGaps() {
      if (!this.navigation.showBackButton) {
        return;
      }
      await navigateToRoute(this.navigation.workspaceId, RouteName.MSP_SECURITY_GAPS_PAGE, {
        securityGapType: this.navigation.securityGapType,
      });
      this.clearNavigationState();
    },
  },
});
